import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, EndpointDisplay, PageSubSection, PatientProfile, ComparisonTable, ReferencesBlock, KMCurve, TextBlock, FeaturedStat, NCCNcallout, ForestPlot } from '../../components';
import MUC from '../../components/StudyDesign/__study-design/muc1';
import { jobCodes } from '../../util/data/jobCodes';
import { StaticImage } from 'gatsby-plugin-image';
import osAcrossPrespecifiedDataTableMob from "../../assets/OS-Forrest-Plot-Composite_Mob.svg";
import osAcrossPrespecifiedDataTableDes from "../../assets/OS-Forrest-Plot-Composite_Desktop.svg";
import styled from 'styled-components';
import { Text, WYSIWYG } from '../../util/typography';

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-A39 - Clinical Trial Findings & Study Design`,
    keywords: `keynote A39, keytruda and enfortumab vedotin clinical findings`,
    description: `Health care professionals can view clinical trial results for KEYNOTE-A39 in adult patients with locally advanced (LA) or metastatic urothelial cancer (mUC).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/urothelial-cancer-first-line-combination-therapy/","@type":"ApprovedIndication","name":"KEYTRUDA® and KEYTRUDA QLEX™ ","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with enfortumab vedotin, for the treatment of adult patients with locally advanced (LA) or metastatic urothelial cancer."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/urothelial-cancer-first-line-combination-therapy/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/urothelial-cancer-first-line-combination-therapy/","@type":"MedicalWebPage","name":"Locally Advanced or Metastatic Urothelial Cancer","description":"First-Line Combination for Advanced Urothelial Cancer"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/urothelial-cancer-first-line-combination-therapy/","@type":"MedicalTrial","name":"Clinical Findings From KEYNOTE⁠-⁠A39","description":"An open-label, randomized, multicenter study of KEYTRUDA + EV vs gem/plat in patients with previously untreated LA/mUC (N=886)","trialDesign":"OpenTrial"}`,
        
    ]
}

//from /src/util/data/indications.js
const indicationId = 36; 
const jobCode = jobCodes[15].jobCode; 

const LimitationContainer = styled.div`
    color: #444444;
    @media (min-width: 320px) and (max-width: 1032px) {
        margin-top: calc(25.51px + 1.4vw) auto;
    }
`;

const CustomMargin = styled.div`
    margin-bottom:40px !important;
    @media (max-width:767px){
    margin-bottom:40px !important;
    }
`;

const PageSectionCustom = styled.div`
> [class^='ComponentWrapper-']{
    margin-bottom:0;
}
[class^='ComponentWrapper-'] [class^='ComponentWrapper-']:last-child{
    margin-bottom:0;
}
    [class^='Heading__Subtitle-']{
    margin-top:0 ;
     
    }
    [class^='StudyDesign__MultiFlowItem-']:last-child [class^='Heading__Subtitle-']{
    @media (max-width:767px){
     margin-top: 30px;
    }
    }
    [class^='StudyDesign__MultiFlowWrapper-'] [class^='StudyDesign__MultiFlowItem-']:first-child {
    margin-top:0; 
    @media (max-width:767px){
    margin-top:0;
    }
}
`;
const KMOSchartAttribution= styled(Text.Footnote)`
    display: block;
    margin-top: calc(-40.51px + 1.40vw);
    margin-bottom: calc(25.51px + 1.40vw);
`

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const NCCNcalloutReferences = [
    {
        label: "8.",
        text: "Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Bladder Cancer V.1.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed March 17, 2026. To view the most recent and complete version of the guideline, go online to NC‍CN‍.‍org. NCCN = National Comprehensive Cancer Network."
    },
];
const NCCNcalloutData = {
    title: 'Select recommendations from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Bladder Cancer<sup>8</sup>',
    bodyCopy: `<h5>Pembrolizumab and enfortumab vedotin: THE ONLY NCCN CATEGORY 1, PREFERRED, 1L SYSTEMIC THERAPY OPTION for patients with locally advanced or metastatic urothelial carcinoma</h5><p>Pembrolizumab and berahyaluronidase alfa-pmph subcutaneous injection may be substituted for IV pembrolizumab. Pembrolizumab and berahyaluronidase alfa-pmph has different dosing and administration instructions compared to IV pembrolizumab.</p>`,
    showBadge: false,
};

const NccnCalloutDefinitions = `<p>Category 1 = based upon high-level evidence (≥1 randomized phase 3 trials or high-quality, robust meta-analyses), there is uniform NCCN consensus (≥85% support of the Panel) that the intervention is appropriate. 1L = first line; NCCN = National Comprehensive Cancer Network® (NCCN®).</p>
<p>According to NCCN, preferred intervention = intervention that is based on superior efficacy, safety, and evidence; and, when appropriate, affordability.</p>
<p>NCCN makes no warranties of any kind whatsoever regarding their content, use, or application and disclaims any responsibility for their application or use in any way.</p>
`;

const pageReferences = [
    {
        label: "1.",
        text: 'Powles T, Valderrama BP, Gupta S, et al. Enfortumab vedotin and pembrolizumab in untreated advanced urothelial cancer. <i>N Engl J Med.</i> 2024;390(10):875-888. doi:10.1056/NEJMoa2312117'
    },
    {
        label: '2.',
        text: 'van der Heijden AG, Bruins HM, Carrion A, et al. EAU guidelines on muscle-invasive and metastatic bladder cancer. European Association of Urology. Limited update March 2025. Accessed September 11, 2025. https://d56bochluxqnz.cloudfront.net/documents/full-guideline/EAU-Guidelines-on-Muscle-Invasive-Bladder-Cancer-2025.pdf'
    },
    {
        label: "3.",
        text: 'Powles T, Bellmunt J, Comperat E, et al. ESMO Clinical Practice Guideline interim update on first-line therapy in advanced urothelial carcinoma. <i>Ann Oncol.</i> 2024;35:485-490. doi.org/10.1016/j.annonc.2024.03.001'
    },
    {
        label: "4.",
        text: 'Powles TB, Van der Heijden MS, Loriot Y, et al. Enfortumab vedotin plus pembrolizumab in untreated locally advanced or metastatic urothelial carcinoma: 2.5-year median follow-up of the phase III EV-302/KEYNOTE-A39 trial. <i>Ann Oncol.</i> 2025;36(10):1212-1219. doi:10.1016/j.annonc.2025.05.536'
    },
    {
        label: "5.",
        text: 'Powles TB, Van der Heijden MS, Loriot K, et al. Supplement to: Enfortumab vedotin plus pembrolizumab in untreated locally advanced or metastatic urothelial carcinoma: 2.5-year median follow-up of the phase III EV-302/KEYNOTE-A39 trial. <i>Ann Oncol.</i> 2025;36(10):1212-1219. doi:10.1016/j.annonc.2025.05.536'
    },
    {
        label: "6.",
        text: 'Powles T, Valderrama BP, Gupta S, et al. Protocol for: Enfortumab vedotin and pembrolizumab in untreated advanced urothelial cancer. <i>N Engl J Med.</i> 2024;390(10):875-888. doi:10.1056/NEJMoa2312117'   
    },
    {
        label: "7.",
        text: 'Powles T, Valderrama BP, Gupta S, et al. Supplementary appendix to: Enfortumab vedotin and pembrolizumab in untreated advanced urothelial cancer. <i>N Engl J Med.</i> 2024;390:875- 888. doi:10.1056/NEJMoa2312117'
    },
    {
        label: "8.",
        text: 'Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Bladder Cancer V.1.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed March 17, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org.'
    },
    
]
const crossLinkContent = {
    sections: [
        {
            heading: "Additional Clinical Data for KEYTRUDA",
            items: [
                { text: '1L Monotherapy for Advanced Urothelial Carcinoma', url: '/efficacy/urothelial-carcinoma-first-line-monotherapy/' },
                { text: '2L Metastatic Urothelial Carcinoma Monotherapy Post-Platinum Failure', url: '/efficacy/urothelial-carcinoma-second-line-monotherapy/#study-design-for-keynote-045' },

            ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/urothelial-cancer-first-line-combination-therapy/' },
                { text: 'Dosing', url: '/dosing/options/#uc' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
}

const anchorLinkModelData = [
    { label: 'Clinical Findings From KEYNOTE&#8288;-&#8288;A39<sup>a</sup> for KEYTRUDA + EV' },
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;A39' },
    { label: 'NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)'},
]

const KMCurveFootnotes = [
    {
        label: "a.",
        text: "KEYNOTE-A39 is also known as EV-302.<sup>1</sup>"
    },
    {
        label: "b.",
        text: "Based on the stratified Cox proportional hazard regression model."
    },
    {
        label: "c.",
        text: "Two-sided <i>P</i> value based on stratified log-rank test."
    },
]

const KMCurveSecondaryCallout = {
    title: "Nearly 2x the median OS",
    text: `<b>31.5 months</b> median OS with KEYTRUDA + EV vs <b>16.1 months</b> with gem/plat.`,
    alt: ""

}

const KMCurveFootnotesProgressionFree = [
    {
        label: "a.",
        text: "KEYNOTE-A39 is also known as EV-302.<sup>1</sup>"
    },
    {
        label: "b.",
        text: "Based on the stratified Cox proportional hazard regression model."
    },
    {
        label: "c.",
        text: "Two-sided <i>P</i> value based on stratified log-rank test."
    },
]

const KMCurveSecondaryCalloutProgressionFree = {
    title: "Nearly 2x the median PFS",
    text: `<b>12.5 months</b> median PFS with KEYTRUDA + EV vs <b>6.3 months</b> with gem/plat.`,
    alt: ""
}

const KMCurveAdditionalInformationProgressionFree = [ ]

const DORComparisonFootnotes = [
    {
        label: 'a.',
        text: 'Includes only patients with measurable disease at baseline (n=437 for KEYTRUDA + EV, n=441 for gem/plat) and is based on patients with best overall response as confirmed complete or partial response.'
    },
    {
        label: 'b.',
        text: 'Two-sided <i>P</i> value based on Cochran-Mantel-Haenszel test stratified by PD&#8288;-&#8288;L1 expression, cisplatin eligibility, and liver metastases.'
    },
]
const DORComparisonDefinition = "1L = first line; CI = confidence interval; CPS = combined positive score; HR = hazard ratio; NE = not estimable; NR = not reached; OS = overall survival."
const KMDefinitionOP = `<ul>
                            <li>HR=0.51 (95% CI, 0.43–0.61)</li>
                            <li>Median OS (95% CI):
                                <ul>
                                    <li>KEYTRUDA + EV: 33.8 months (26.1–39.3)</li>
                                    <li>gem/plat: 15.9 months (13.6–18.3)</li>
                                </ul>
                            </li>
                            <li>Events Observed: 203/442 (45.9%) with KEYTRUDA + EV and 297/444 (66.9%) with gem/plat</li>
                        </ul`
const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results for KEYTRUDA® (pembrolizumab)" isBranding/>
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light" heading={'H3'}>KEYTRUDA + enfortumab vedotin (EV): A 1L Standard of Care for Patients With Locally Advanced (LA) or Metastatic Urothelial Cancer (mUC)<sup>1-3</sup></PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">

                        <PageSection title={anchorLinkModelData[0].label}>
                            <CustomMargin>
                            <KMCurve
                                title="Kaplan-Meier Curve Estimates of Overall Survival in KEYNOTE&#8288;-&#8288;A39"
                                hr="HR<sup>b</sup>=0.47; 95% CI, 0.38&ndash;0.58; <i>P</i><sup>c</sup><0.0001"
                                limitation='Superior OS with KEYTRUDA + EV vs gemcitabine and cisplatin or carboplatin (gem/plat)'
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/OS-KM_A39_PI_v6.png"
                                    alt="Kaplan-Meier Estimates of Overall Survival (OS) With KEYTRUDA® (pembrolizumab) + Enfortumab Vedotin in KEYNOTE-A39" />}
                            />

                            <FeaturedStat
                                statistic="53%"
                                title="Reduction in risk of death with KEYTRUDA + EV vs gem/plat"
                                hr="HR<sup>b</sup>=0.47 (95% CI, 0.38&ndash;0.58; <i>P</i><sup>c</sup><0.0001). Events observed: 133/442 (30%) with KEYTRUDA + EV vs 226/444 (51%) with gem/plat."
                            />

                            <SecondaryCallout 
                                title= "Nearly 2x the median OS"
                                text= "<b>31.5 months</b> median OS with KEYTRUDA + EV vs <b>16.1 months</b> with gem/plat."
                                alt= ""
                                additionalInformation={[
                                    {
                                        text: `
                                            <ul>
                                                <li>Efficacy results were consistent across all stratified patient subgroups.</li>
                                            </ul>
                                        `
                                    },
                                ]}
                                footnotes={KMCurveFootnotes}
                            />
                        </CustomMargin>
                        </PageSection>

                        <PageSection title={`Exploratory Analysis With 2.5&#8288;-&#8288;Year Follow&#8288;-&#8288;Up<sup>4</sup>`}>
                            <KMCurve
                                title='Kaplan-Meier Estimates of OS in the Overall Population (2.5-Year Follow-Up)<sup>4</sup>'
                                hr='HR=0.51 (95% CI, 0.43–0.61)'
                                limitation='<b>LIMITATIONS:</b> This post hoc analysis (median follow-up time: 29.1 months) in KEYNOTE-A39 was exploratory in nature and occurred after the protocol-specified final analysis. There was no formal statistical testing planned for this analysis. Therefore, results should be interpreted with caution and no conclusions should be drawn.'
                                image={() => <StaticImage
                                    placeholder=''
                                    src='../../assets/KM_OS_25.png'
                                    alt='Kaplan-Meier Estimates of Overall Survival (OS) in the Overall Population (2.5-Year Follow-Up)'
                                 />}
                                additionalInformation={[{text: KMDefinitionOP}]}
                            />
                            <KMOSchartAttribution>From: <i>Ann Oncol.</i> Powles TB, Van der Heijden MS, Loriot Y, et al. Enfortumab vedotin plus pembrolizumab in untreated locally advanced or metastatic urothelial carcinoma: 2.5-year median follow-up of the phase III EV-302/KEYNOTE-A39 trial. 2025;36(10):1212-1219. doi:10.1016/j.annonc.2025.05.536</KMOSchartAttribution>
                            <ForestPlot
                                    title="OS Across Additional Subgroups (2.5-Year Follow-Up)<sup>4</sup>"
                                    limitation='<b>LIMITATIONS:</b> This post hoc analysis (median follow-up time: 29.1 months) in KEYNOTE-A39 was exploratory in nature and occurred after the protocol-specified final analysis. The study was not powered to detect differences in the treatment effect based on subgroups. There was no formal statistical testing planned for this analysis. Therefore, results should be interpreted with caution and no conclusions should be drawn.'
                                    image={() => <StaticImage
                                    placeholder=''
                                    src='../../assets/GU_KN-A39_CFL_OS_Forrest_Plot_Composite_08.jpg'
                                    alt='Overall Survival (OS) Across Additional Subgroups in KEYNOTE-A39 (2.5-Year Follow-Up)'
                                 />}
                                 
                                    // image={
                                    //     ()=>
                                    //         <picture>
                                    //             <source srcSet='../../assets/GU_KN-A39_CFL_OS Forrest_Plot_Composite_08.jpg' media='(max-width: 768px)' type="image/webp" />
                                    //             <img style={{width:"100%"}} src='../../assets/GU_KN-A39_CFL_OS_Forrest_Plot_Composite_08.jpg' alt="" />
                                    //         </picture>
                                        
                                    // }
                                    chartAttribution="From: <i>Ann Oncol.</i> Powles TB, Van der Heijden MS, Loriot Y, et al. Enfortumab vedotin plus pembrolizumab in untreated locally advanced or metastatic urothelial carcinoma: 2.5-year median follow-up of the phase III EV-302/KEYNOTE-A39 trial. 2025;36(10):1212-1219. doi:10.1016/j.annonc.2025.05.536"
                            />
                            {/* <TextBlock><b>LIMITATIONS:</b> This post hoc analysis (median follow-up time: 29.1 months) in KEYNOTE-A39 was exploratory in nature and occurred after the protocol-specified final analysis. The study was not powered to detect differences in the treatment effect based on cisplatin eligibility. There was no formal statistical testing planned for this analysis. Therefore, results should be interpreted with caution and no conclusions should be drawn</TextBlock> */}
                            <KMCurve
                                title="Kaplan-Meier Estimates of OS for Patients Who Were Cisplatin-Eligible (2.5-Year Follow-Up)<sup>4,5</sup>"
                                hr="HR=0.54 (95% CI, 0.42–0.70)"
                                limitation='<b>LIMITATIONS:</b> This post hoc analysis (median follow-up time: 29.1 months) in KEYNOTE-A39 was exploratory in nature and occurred after the protocol-specified final analysis. The study was not powered to detect differences in the treatment effect based on cisplatin eligibility. There was no formal statistical testing planned for this analysis. Therefore, results should be interpreted with caution and no conclusions should be drawn.'
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/WTNY-P00105400_A39_OS_KM_nejmoa2312117_A_r8_7.8pt.png"
                                    alt="Kaplan-Meier Estimates of Overall Survival (OS) for Patients Who Were Cisplatin-Eligible in KEYNOTE-A39 (2.5-Year Follow-Up)" />}
                                additionalInformation={[
                                    {
                                        text: `
                                            <ul>
                                                <li>HR=0.54 (95% CI, 0.42–0.70)</li>
                                                <li>Median OS (95% CI):
                                                    <ul>
                                                        <li>KEYTRUDA + EV: 36.7 months (31.5–NE)</li>
                                                        <li>gem/plat: 18.7 months (16.6–22.1)</li>
                                                    </ul>
                                                </li>
                                                <li>Events Observed: 101/244 (41.4%) with KEYTRUDA + EV and 143/234 (61.1%) with gem/plat</li>
                                            </ul>  
                                        `
                                    },
                                ]}
                            />
                            <KMOSchartAttribution>From: <i>Ann Oncol.</i> Powles TB, Van der Heijden MS, Loriot Y, et al. Supplement to: Enfortumab vedotin plus pembrolizumab in untreated locally advanced or metastatic urothelial carcinoma: 2.5-year median follow-up of the phase III EV-302/KEYNOTE-A39 trial. 2025;36(10):1212-1219. doi:10.1016/j.annonc.2025.05.536</KMOSchartAttribution>
                            <KMCurve
                                title="Kaplan-Meier Estimates of OS for Patients Who Were Cisplatin-Ineligible (2.5-Year Follow-Up)<sup>4,5</sup>"
                                hr="HR=0.50 (95% CI, 0.39–0.64)"
                                limitation="<b>LIMITATIONS:</b> This post hoc analysis (median follow-up time: 29.1 months) in KEYNOTE-A39 was exploratory in nature and occurred after the protocol-specified final analysis. The study was not powered to detect differences in the treatment effect based on cisplatin eligibility. There was no formal statistical testing planned for this analysis. Therefore, results should be interpreted with caution and no conclusions should be drawn."
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/WTNY-P00105400_A39_OS_KM_nejmoa2312117_B_r9_7.8pt.png"
                                    alt="Kaplan-Meier Estimates of Overall Survival (OS) for Patients Who Were Cisplatin-Ineligible in KEYNOTE-A39 (2.5-Year Follow-Up)" />}
                                additionalInformation={[
                                    {
                                        text: `
                                            <ul>
                                                <li>HR=0.50 (95% CI, 0.39–0.64)</li>
                                                <li>Median OS (95% CI):
                                                    <ul>
                                                        <li>KEYTRUDA + EV: 25.6 months (22.7–36.1)</li>
                                                        <li>gem/plat: 12.7 months (11.0–14.7)</li>
                                                    </ul>
                                                </li>
                                                <li>Events Observed: 102/198 (51.5%) with KEYTRUDA + EV and 154/210 (73.3%) with gem/plat</li>
                                            </ul>
                                        `
                                    },
                                ]}
                            />
                            <KMOSchartAttribution>From: <i>Ann Oncol.</i> Powles TB, Van der Heijden MS, Loriot Y, et al. Supplement to: Enfortumab vedotin plus pembrolizumab in untreated locally advanced or metastatic urothelial carcinoma: 2.5-year median follow-up of the phase III EV-302/KEYNOTE-A39 trial. 2025;36(10):1212-1219. doi:10.1016/j.annonc.2025.05.536</KMOSchartAttribution>
                        </PageSection>

                        <PageSection>
                            <KMCurve
                                title="Kaplan-Meier Estimates for Progression-Free Survival (PFS) in KEYNOTE&#8288;-&#8288;A39<sup>a</sup>"
                                limitation='Superior PFS with KEYTRUDA + EV vs gemcitabine and cisplatin or carboplatin (gem/plat)'
                                hr="HR<sup>b</sup>=0.45; 95% CI, 0.38–0.54; <i>P</i><sup>c</sup><0.0001"
                                image={() => <StaticImage
                                    placeholder="blurred"
                                    src="../../assets/PFS-KM_A39_PI_v6.png"
                                    alt="Kaplan-Meier Estimates for Progression-Free Survival (PFS) With KEYTRUDA® (pembrolizumab) + Enfortumab Vedotin in KEYNOTE-A39" />}
                            />

                            <FeaturedStat
                                statistic="55%"
                                title="Reduction in risk of disease progression or death with KEYTRUDA + EV vs gem/plat"
                                hr="HR<sup>b</sup>=0.45 (95% CI, 0.38&ndash;0.54; <i>P</i><sup>c</sup><0.0001). Events observed: 223/442 (50%) with KEYTRUDA + EV vs 307/444 (69%) with gem/plat."
                            />
                            <SecondaryCallout 
                                title= "Nearly 2x the median PFS"
                                text="<b>12.5 months</b> median PFS with KEYTRUDA + EV vs <b>6.3 months</b> with gem/plat."
                                alt=""
                                additionalInformation={[
                                    {
                                        text: `
                                            <ul>
                                                <li>Efficacy results were consistent across all stratified patient subgroups.</li>
                                            </ul>
                                        `
                                    },
                                ]}
                                footnotes={KMCurveFootnotesProgressionFree}
                            />
                           

                           

                            < EndpointDisplay 
                                title="Superior Objective Response Rate (ORR) With KEYTRUDA + EV vs gem/plat<sup>a,b</sup>"
                                limitation='68% of patients responded to KEYTRUDA + EV vs 44% with gem/plat'
                                middleP="<sup>b</sup><0.0001"
                                definitions={DORComparisonDefinition} 
                                footnotes={DORComparisonFootnotes} 
                                additionalInformation={[
                                    {
                                        title: '',
                                        text: `
                                            <ul>
                                                <li>Efficacy results were consistent across all stratified patient subgroups.</li>
                                            </ul>
                                        `
                                    },
                                ]}  
                                cards={
                                    [
                                        {
                                            dataType: 'ORR',
                                            label: 'KEYTRUDA + EV',
                                            rate: '68%',
                                            ci: '95% CI: 63&ndash;72',
                                            completeResponseRate: '29%',
                                            partialResponseRate: '39%',
                                        },
                                        {
                                            dataType: 'ORR',
                                            label: 'gem/plat',
                                            rate: '44%',
                                            ci: '95% CI: 40&ndash;49',
                                            completeResponseRate: '12%',
                                            partialResponseRate: '32%',
                                        }
                                    ]
                                }
                            />
                        </PageSection>
                        <PageSection bgColor="cloud" title={anchorLinkModelData[1].label}>
                        <PageSectionCustom>
                            <MUC />
                        </PageSectionCustom>
                        </PageSection>

                        <PageSection  title={anchorLinkModelData[2].label}>
                            <NCCNcallout
                                data={NCCNcalloutData}
                                references={NCCNcalloutReferences}
                                definitions={NccnCalloutDefinitions}
                            />
                        </PageSection>

                    </TemplateColumn>
                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;